import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Page from '../../components/Page'
import Card from '../../components/Card'
import PeriodSelector from './components/PeriodSelector'
import { useOwner } from '../../contexts/OwnerContext'
import { useAI } from '../../hooks/useAI'
import { useLocation } from 'react-router-dom'
import { config } from '../../config'
import { normalizeScreenContext, getContextDisplayName } from '../../utils/contextNormalizer'
import {
  Brain,
  Loader,
  Sparkles,
  ChevronRight,
  Lightbulb,
  Zap,
  Star,
  History,
  Search,
  AlertCircle,
  X,
  ChevronDown,
  Plus
} from 'lucide-react'

interface MonthRange {
  startMonth: number
  endMonth: number
}

interface StoredQuestion {
  id: string
  question: string
  answer?: string
  timestamp: Date
  context?: string
}

const SpecializedQuestionsPage: React.FC = () => {
  const { headers } = useOwner()
  const location = useLocation()
  const { messages, isLoading, error, askQuestion, clearError, clearMessages } = useAI()

  // Estados
  const [questionText, setQuestionText] = useState('')
  const [screenContext, setScreenContext] = useState<string>('Specialized Questions')
  const [storedQuestions, setStoredQuestions] = useState<StoredQuestion[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [selectedMonthRanges, setSelectedMonthRanges] = useState<MonthRange[]>([])
  const [usageAlert, setUsageAlert] = useState<string | null>(null)
  const [usageStats, setUsageStats] = useState<{
    requests_this_minute: number
    requests_today: number
    minute_limit: number
    daily_limit: number
    minute_percent: number
    daily_percent: number
  } | null>(null)
  const [selectedQuestionAnswer, setSelectedQuestionAnswer] = useState<string | null>(null)
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null)
  const [selectedQuestionText, setSelectedQuestionText] = useState<string | null>(null)
  const [isQuestionCardExpanded, setIsQuestionCardExpanded] = useState(true)

  // Converter período (year + monthRanges) para start_date e end_date
  const convertPeriodToDates = useCallback((year: number, monthRanges: MonthRange[]): { startDate: string; endDate: string } | null => {
    if (monthRanges.length === 0) {
      return null
    }

    // Ordenar e obter todos os meses selecionados
    const sortedMonths = monthRanges.flatMap(range =>
      Array.from({ length: range.endMonth - range.startMonth + 1 }, (_, i) => range.startMonth + i)
    ).sort((a, b) => a - b)

    if (sortedMonths.length === 0) {
      return null
    }

    const firstMonth = sortedMonths[0]
    const lastMonth = sortedMonths[sortedMonths.length - 1]

    // Primeiro dia do primeiro mês selecionado
    const start = new Date(year, firstMonth - 1, 1)
    // Último dia do último mês selecionado
    const end = new Date(year, lastMonth, 0)

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    }
  }, [])

  // Carregar período salvo do localStorage ao montar o componente
  useEffect(() => {
    const PERIOD_STORAGE_KEY = `lognex-question-period-${headers?.company || 'default'}`
    
    try {
      const savedPeriod = localStorage.getItem(PERIOD_STORAGE_KEY)
      if (savedPeriod) {
        const period = JSON.parse(savedPeriod)
        if (period.year && period.monthRanges && period.monthRanges.length > 0) {
          setSelectedYear(period.year)
          setSelectedMonthRanges(period.monthRanges)
          
          // Converter período para datas
          const dates = convertPeriodToDates(period.year, period.monthRanges)
          if (dates) {
            setStartDate(dates.startDate)
            setEndDate(dates.endDate)
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar período salvo:', error)
    }
  }, [headers?.company, convertPeriodToDates])


  // Pegar contexto passado pela navegação
  useEffect(() => {
    const state = location.state as { screenContext?: string } | null
    if (state?.screenContext) {
      // Normalizar contexto usando função centralizada
      const normalized = normalizeScreenContext(state.screenContext)
      setScreenContext(normalized)
    }
  }, [location.state])

  // Função para carregar perguntas do backend
  const loadStoredQuestions = useCallback(async () => {
    if (!headers?.userToken || !screenContext) {
      // Se não tiver contexto ou token, não buscar
      return
    }

    try {
      console.log('📋 Carregando perguntas para contexto:', screenContext)
      const response = await fetch(
        `${config.API_BASE_URL}/question/list?screen_context=${encodeURIComponent(screenContext)}&source=specialized&limit=50&skip=0`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${headers.userToken}`,
            'company': headers.company || '',
            'companyName': headers.companyName || '',
            'businessUnit': headers.businessUnit || '-1',
            'logisticOperator': headers.logisticOperator || '-1',
            'user': headers.user || '',
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        const questions: StoredQuestion[] = data.questions.map((q: any) => ({
          id: String(q.id),
          question: q.question,
          answer: q.answer || undefined,
          timestamp: q.created_at ? new Date(q.created_at) : new Date(),
          context: q.screen_context || screenContext,
        }))
        console.log('✅ Perguntas carregadas:', questions.length)
        setStoredQuestions(questions)
      } else {
        console.log('⚠️ Resposta não OK ao carregar perguntas:', response.status)
        setStoredQuestions([])
      }
    } catch (error) {
      console.error('❌ Erro ao carregar perguntas:', error)
      setStoredQuestions([])
    }
  }, [screenContext, headers])

  // Carregar perguntas armazenadas do backend quando headers e contexto estiverem disponíveis
  useEffect(() => {
    // Garantir que headers esteja completamente carregado antes de buscar
    if (headers && headers.userToken && screenContext) {
      loadStoredQuestions()
    }
  }, [headers, screenContext, loadStoredQuestions])

  // Handler para o filtro de período
  const handlePeriodFilter = useCallback((year: number, monthRanges: MonthRange[]) => {
    setSelectedYear(year)
    setSelectedMonthRanges(monthRanges)
    
    // Converter período para datas
    const dates = convertPeriodToDates(year, monthRanges)
    if (dates) {
      setStartDate(dates.startDate)
      setEndDate(dates.endDate)
    } else {
      setStartDate('')
      setEndDate('')
    }

    // Salvar período no localStorage se houver seleção válida
    const PERIOD_STORAGE_KEY = `lognex-question-period-${headers?.company || 'default'}`
    if (year > 0 && monthRanges.length > 0) {
      try {
        localStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify({
          year,
          monthRanges
        }))
      } catch (error) {
        console.error('Erro ao salvar período:', error)
      }
    } else {
      // Limpar período salvo se não houver seleção
      try {
        localStorage.removeItem(PERIOD_STORAGE_KEY)
      } catch (error) {
        console.error('Erro ao limpar período:', error)
      }
    }
  }, [headers?.company, convertPeriodToDates])

  const normalizedContext = (screenContext || '').toUpperCase()
  const requiresPeriod = !['DISTRIBUTIONCENTERS', 'CLIENTS'].includes(normalizedContext)
  // Verificar se pode enviar pergunta (pergunta preenchida E período selecionado quando exigido)
  const canSendQuestion = questionText.trim().length > 0 && (!requiresPeriod || (startDate && endDate))

  // Enviar pergunta
  const handleSendQuestion = async () => {
    if (!canSendQuestion || isLoading) {
      return
    }

    // Guardar texto da pergunta antes de limpar
    const currentQuestion = questionText
    // Limpar resposta anterior para evitar exibir a última resposta enquanto carrega
    setSelectedQuestionAnswer(null)
    setSelectedQuestionId(null)
    clearMessages()

    // Usar datas do período selecionado
    const response = await askQuestion(
      questionText,
      screenContext,
      requiresPeriod ? startDate : undefined,
      requiresPeriod ? endDate : undefined,
      true
    ) as any

    // Capturar informações de uso do tier gratuito
    if (response) {
      setUsageAlert(response.usage_alert || null)
      setUsageStats(response.usage_stats || null)
    }

    setQuestionText('')
    setSelectedQuestionAnswer(null) // Limpar resposta do histórico
    setSelectedQuestionId(null) // Limpar seleção do histórico
    setSelectedQuestionText(currentQuestion) // Mostrar a pergunta enviada no card de resposta
    setIsQuestionCardExpanded(false) // Colapsar card de pergunta após enviar

    // Recarregar perguntas após criar nova pergunta
    setTimeout(() => {
      loadStoredQuestions()
    }, 1000) // Aguardar 1s para garantir que a pergunta foi salva
  }

  // Usar pergunta do histórico
  const handleUseStoredQuestion = (question: StoredQuestion) => {
    setQuestionText(question.question)
    setSelectedQuestionId(question.id) // Marcar pergunta como selecionada
    setSelectedQuestionText(question.question) // Guardar texto da pergunta selecionada

    // Se a pergunta tiver resposta armazenada, mostrar no card de resposta
    if (question.answer) {
      setSelectedQuestionAnswer(question.answer)
      setIsQuestionCardExpanded(false) // Colapsar card de pergunta quando mostrar resposta
    } else {
      setSelectedQuestionAnswer(null)
      setIsQuestionCardExpanded(true) // Expandir se não houver resposta
    }
  }

  // Expandir card de pergunta para nova pergunta
  const handleNewQuestion = () => {
    setIsQuestionCardExpanded(true)
    setSelectedQuestionAnswer(null)
    setSelectedQuestionId(null)
    setSelectedQuestionText(null)
    setQuestionText('')
  }

  // Filtrar perguntas
  const filteredQuestions = storedQuestions.filter(q =>
    q.question.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Formatar data relativa
  const formatRelativeTime = (date: Date) => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Hoje'
    if (diffDays === 1) return 'Ontem'
    if (diffDays < 7) return `${diffDays} dias atrás`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas atrás`
    return `${Math.floor(diffDays / 30)} meses atrás`
  }

  // Obter última resposta da IA
  const lastResponse = messages.filter(m => m.type === 'assistant').slice(-1)[0]
  
  // Se houver resposta selecionada do histórico, usar ela
  const displayResponse = selectedQuestionAnswer 
    ? { 
        content: selectedQuestionAnswer, 
        type: 'assistant' as const,
        cached: true,
        id: 'stored-answer'
      }
    : lastResponse

  // Colapsar card de pergunta quando houver resposta exibida
  useEffect(() => {
    if (displayResponse || isLoading) {
      setIsQuestionCardExpanded(false)
    }
  }, [displayResponse, isLoading])

  // Sugestões de perguntas (por contexto)
  const suggestedQuestionsByContext: Record<string, string[]> = {
    CTES: [
      'Quais são as rotas com maior custo por km?',
      'Quem são os principais remetentes e destinatários no período?',
      'Qual a tendência de valor de frete nos últimos meses?'
    ],
    NFES: [
      'Quais clientes têm maior potencial de crescimento?',
      'Quais são os emitentes com maior valor total no período?',
      'Qual é a distribuição de peso por destinatário?'
    ],
    CLIENTS: [
      'Quais clientes têm maior recorrência de compra?',
      'Quais clientes concentram o maior faturamento?',
      'Quais clientes estão em queda de volume no período?'
    ],
    LOGISTICS: [
      'Quais são os principais gargalos logísticos?',
      'Como está a eficiência operacional das rotas?',
      'Quais regiões têm maior concentração de entregas?'
    ],
    FREIGHT: [
      'Quais fretes têm maior custo médio?',
      'Como evoluiu o custo de frete por km no período?',
      'Quais rotas têm maior impacto no custo total?'
    ],
    VEHICLES: [
      'Quais veículos têm maior custo por km?',
      'Como está a utilização média da frota?',
      'Quais tipos de veículo concentram mais viagens?'
    ],
    DISTRIBUTIONCENTERS: [
      'Quais CDs têm maior abrangência (mais estados atendidos) e por quê?',
      'Em quais estados há lacunas de cobertura que justificam abrir um novo CD?',
      'Quais CDs têm maior valor estratégico pela posição geográfica e cobertura?',
      'Quais CDs estão sem geolocalização e impactam análises de roteirização?',
      'Qual o nível de adoção de IA por CD e quais são os outliers?'
    ],
    COSTS: [
      'Qual a tendência de custos para o próximo trimestre?',
      'Quais componentes mais impactam o custo total?',
      'Onde estão as maiores oportunidades de redução?'
    ],
    SALESFORCE: [
      'Quais regiões têm maior crescimento de vendas?',
      'Quais vendedores têm melhor performance no período?',
      'Qual a tendência de conversão por região?',
      'Qual a previsão de custo de frete para os próximos períodos com base no histórico?'
    ],
    DEFAULT: [
      'Quais clientes têm maior potencial de crescimento?',
      'Como está a eficiência operacional das rotas?',
      'Quais são os principais gargalos logísticos?'
    ]
  }

  const contextKey = (screenContext || 'DEFAULT').toUpperCase()
  const suggestedQuestions = suggestedQuestionsByContext[contextKey] || suggestedQuestionsByContext.DEFAULT

  // Obter nome do contexto para exibição
  const contextDisplayName = getContextDisplayName(screenContext)
  const pageTitle = contextDisplayName !== 'Geral' 
    ? `Perguntas Especializadas - ${contextDisplayName}`
    : 'Perguntas Especializadas'
  const pageSubtitle = contextDisplayName !== 'Geral'
    ? `Faça perguntas inteligentes sobre ${contextDisplayName} e receba análises detalhadas`
    : 'Faça perguntas inteligentes sobre seus dados e receba análises detalhadas'

  return (
    <Page
      title={pageTitle}
      subtitle={pageSubtitle}
      icon={Sparkles}
    >
            {/* Info da Empresa */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-6 mb-6"
            >
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                  {headers?.companyName || headers?.company || 'Empresa'}
                </span>
              </div>
            </motion.div>

            {/* Alerta de Uso do Tier Gratuito */}
            {usageAlert && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-6"
              >
                <Card className="overflow-hidden" hover={false}>
                  <div
                    className={`px-6 py-4 flex items-start gap-4 ${
                      usageAlert.includes('CRÍTICO') || usageAlert.includes('LIMITE ATINGIDO')
                        ? 'border-l-4'
                        : ''
                    }`}
                    style={{
                      backgroundColor: usageAlert.includes('CRÍTICO') || usageAlert.includes('LIMITE ATINGIDO')
                        ? 'rgba(239, 68, 68, 0.1)'
                        : 'rgba(245, 158, 11, 0.1)',
                      borderLeftColor: usageAlert.includes('CRÍTICO') || usageAlert.includes('LIMITE ATINGIDO')
                        ? '#ef4444'
                        : '#f59e0b',
                      borderLeftWidth: usageAlert.includes('CRÍTICO') || usageAlert.includes('LIMITE ATINGIDO') ? '4px' : '0px'
                    }}
                  >
                    <AlertCircle
                      className={`w-6 h-6 flex-shrink-0 mt-0.5 ${
                        usageAlert.includes('CRÍTICO') || usageAlert.includes('LIMITE ATINGIDO')
                          ? 'text-red-500'
                          : 'text-yellow-500'
                      }`}
                    />
                    <div className="flex-1">
                      <h4
                        className="font-bold text-base mb-1"
                        style={{
                          color: usageAlert.includes('CRÍTICO') || usageAlert.includes('LIMITE ATINGIDO')
                            ? '#ef4444'
                            : '#f59e0b'
                        }}
                      >
                        {usageAlert.includes('CRÍTICO') ? '⚠️⚠️⚠️ Alerta Crítico' : usageAlert.includes('LIMITE ATINGIDO') ? '🚫 Limite Atingido' : '⚠️ Alerta de Uso'}
                      </h4>
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                        {usageAlert.replace(/⚠️|🚫|🚨/g, '').trim()}
                      </p>
                      {usageStats && (
                        <div className="mt-3 flex flex-wrap gap-4 text-xs">
                          <div>
                            <span style={{ color: 'var(--text-secondary)' }}>Hoje: </span>
                            <span
                              className="font-semibold"
                              style={{
                                color: usageStats.daily_percent >= 95
                                  ? '#ef4444'
                                  : usageStats.daily_percent >= 80
                                  ? '#f59e0b'
                                  : '#10b981'
                              }}
                            >
                              {usageStats.requests_today}/{usageStats.daily_limit} ({usageStats.daily_percent.toFixed(1)}%)
                            </span>
                          </div>
                          <div>
                            <span style={{ color: 'var(--text-secondary)' }}>Este minuto: </span>
                            <span
                              className="font-semibold"
                              style={{
                                color: usageStats.minute_percent >= 95
                                  ? '#ef4444'
                                  : usageStats.minute_percent >= 80
                                  ? '#f59e0b'
                                  : '#10b981'
                              }}
                            >
                              {usageStats.requests_this_minute}/{usageStats.minute_limit} ({usageStats.minute_percent.toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setUsageAlert(null)
                        setUsageStats(null)
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </Card>
              </motion.div>
            )}

      {/* Seletor de Período - Fora do card */}
      {requiresPeriod && (
        <div className="mb-6">
          <PeriodSelector
            onPeriodChange={handlePeriodFilter}
            initialYear={selectedYear}
            initialMonthRanges={selectedMonthRanges}
          />
        </div>
      )}

      {/* Layout Principal - Grid Responsivo */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Coluna Principal - 8 colunas */}
        <div className="lg:col-span-8 space-y-6">
          {/* Card de Pergunta */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="overflow-hidden shadow-lg">
              {/* Header do Card */}
              <div
                className="px-6 py-5 border-b flex items-center justify-between"
                style={{
                  borderColor: 'rgba(59, 130, 246, 0.2)',
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.08) 50%, rgba(29, 78, 216, 0.05) 100%)'
                }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="p-3 rounded-xl shadow-md"
                    style={{ 
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.4)'
                    }}
                  >
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                      Faça sua Pergunta
                    </h3>
                    <p className="text-sm font-medium" style={{ color: '#3b82f6' }}>
                      Use linguagem natural para obter insights dos seus dados
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isQuestionCardExpanded && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleNewQuestion}
                      className="px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all duration-200"
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: '#ffffff',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                      }}
                    >
                      <Plus className="w-4 h-4" />
                      Nova Pergunta
                    </motion.button>
                  )}
                  <motion.button
                    onClick={() => setIsQuestionCardExpanded(!isQuestionCardExpanded)}
                    className="p-2 rounded-lg transition-colors duration-200"
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--text-secondary)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <motion.div
                      animate={{ rotate: isQuestionCardExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </motion.div>
                  </motion.button>
                </div>
              </div>

              {/* Área de Input - Colapsável */}
              <AnimatePresence>
                {isQuestionCardExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 space-y-4" style={{ backgroundColor: 'var(--bg-card)' }}>

                <div className="relative">
                  <textarea
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSendQuestion()
                      }
                    }}
                    placeholder="Ex: Qual é a tendência de crescimento das entregas nos últimos 3 meses?"
                    disabled={isLoading}
                    className="w-full px-5 py-4 rounded-xl border-2 resize-none focus:outline-none focus:ring-2 text-base transition-all duration-200 shadow-sm"
                    style={{
                      backgroundColor: questionText ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-secondary)',
                      borderColor: questionText ? '#3b82f6' : 'var(--border-primary)',
                      color: 'var(--text-primary)',
                      minHeight: '120px',
                      boxShadow: questionText ? '0 2px 8px rgba(59, 130, 246, 0.15)' : 'var(--shadow-sm)',
                    }}
                    rows={4}
                  />

                  {/* Contador de caracteres */}
                  <div
                    className="absolute bottom-3 left-5 text-xs"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {questionText.length}/1000
                  </div>
                </div>

                {/* Sugestões Rápidas */}
                <div className="space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Lightbulb className="w-4 h-4" style={{ color: '#f59e0b' }} />
                    Sugestões rápidas:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {suggestedQuestions.map((suggestion, index) => {
                      const textColors = ['#f97316', '#2563eb', '#16a34a', '#7c3aed', '#e11d48']
                      const bgColors = [
                        'rgba(249, 115, 22, 0.08)',
                        'rgba(37, 99, 235, 0.08)',
                        'rgba(22, 163, 74, 0.08)',
                        'rgba(124, 58, 237, 0.08)',
                        'rgba(225, 29, 72, 0.08)'
                      ]
                      const isLast = index === suggestedQuestions.length - 1
                      const textColor = isLast ? '#e11d48' : textColors[index % textColors.length]
                      const bgColor = isLast ? 'rgba(225, 29, 72, 0.08)' : bgColors[index % bgColors.length]
                      return (
                        <motion.button
                          key={index}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => setQuestionText(suggestion)}
                          className="w-full px-4 py-2 rounded-lg text-sm font-medium text-left transition-all duration-200 border"
                          style={{
                            backgroundColor: bgColor,
                            borderColor: textColor,
                            boxShadow: 'var(--shadow-sm)'
                          }}
                        >
                          <span style={{ color: textColor }}>
                            {suggestion}
                          </span>
                        </motion.button>
                      )
                    })}
                  </div>
                </div>

                {/* Botão Enviar */}
                <div className="flex justify-end">
                  <motion.button
                    whileHover={canSendQuestion ? { scale: 1.05, y: -2 } : {}}
                    whileTap={canSendQuestion ? { scale: 0.98 } : {}}
                    onClick={handleSendQuestion}
                    disabled={!canSendQuestion || isLoading}
                    className="px-8 py-3.5 rounded-xl font-bold text-white flex items-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: canSendQuestion 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)'
                        : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 50%, #4b5563 100%)',
                      boxShadow: canSendQuestion ? '0 8px 20px rgba(59, 130, 246, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {isLoading ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Analisando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Enviar Pergunta
                      </>
                    )}
                  </motion.button>
                </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>

          {/* Card de Resposta */}
          <AnimatePresence mode="wait">
            {(displayResponse || isLoading || error) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="overflow-hidden shadow-lg">
                  {/* Header do Card */}
                  <div
                    className="px-6 py-5 border-b flex items-center justify-between"
                    style={{
                      borderColor: error ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                      background: error
                        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.08) 50%, rgba(185, 28, 28, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.08) 50%, rgba(4, 120, 87, 0.05) 100%)'
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="p-3 rounded-xl shadow-md"
                        style={{ 
                          background: error 
                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: error 
                            ? '0 4px 14px 0 rgba(239, 68, 68, 0.4)'
                            : '0 4px 14px 0 rgba(16, 185, 129, 0.4)'
                        }}
                      >
                        {error ? (
                          <AlertCircle className="w-6 h-6 text-white" />
                        ) : (
                          <Zap className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg" style={{ color: error ? '#ef4444' : '#10b981' }}>
                          {error ? 'Erro na Análise' : 'Resposta da IA'}
                        </h3>
                        {selectedQuestionText && !error ? (
                          <p
                            className="text-sm font-medium truncate max-w-md"
                            style={{ color: '#10b981' }}
                            title={selectedQuestionText}
                          >
                            {selectedQuestionText}
                          </p>
                        ) : (
                          <p className="text-sm font-medium" style={{ color: error ? '#ef4444' : '#10b981' }}>
                            {error ? 'Não foi possível processar sua pergunta' : 'Análise gerada com inteligência artificial'}
                          </p>
                        )}
                      </div>
                    </div>
                    {(displayResponse?.cached || selectedQuestionAnswer) && (
                      <span
                        className="px-3 py-1.5 rounded-full text-xs font-bold shadow-sm"
                        style={{ 
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          color: '#ffffff',
                          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                        }}
                      >
                        {selectedQuestionAnswer ? 'Do histórico' : 'Em cache'}
                      </span>
                    )}
                  </div>

                  {/* Conteúdo da Resposta */}
                  <div className="p-6" style={{ backgroundColor: 'var(--bg-card)' }}>
                    {isLoading ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        >
                          <Brain className="w-12 h-12" style={{ color: '#3b82f6' }} />
                        </motion.div>
                        <div className="text-center">
                          <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            Analisando seus dados...
                          </p>
                          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                            A IA está processando sua pergunta
                          </p>
                        </div>
                        <div className="flex gap-1.5">
                          {[0, 1, 2].map((i) => (
                            <motion.div
                              key={i}
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ 
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)'
                              }}
                              animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.2, 1] }}
                              transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                            />
                          ))}
                        </div>
                      </div>
                    ) : error ? (
                      <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                        <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium" style={{ color: '#ef4444' }}>{error}</p>
                          <button
                            onClick={clearError}
                            className="mt-2 text-sm underline"
                            style={{ color: '#ef4444' }}
                          >
                            Tentar novamente
                          </button>
                        </div>
                      </div>
                    ) : displayResponse ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="prose prose-lg max-w-none"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        <div
                          className="p-5 rounded-xl leading-relaxed whitespace-pre-wrap shadow-sm"
                          style={{ 
                            backgroundColor: 'var(--bg-secondary)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            boxShadow: 'var(--shadow-sm)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          {displayResponse.content}
                        </div>

                        {/* Tokens Info */}
                        {((displayResponse && 'tokens_used' in displayResponse && displayResponse.tokens_used && displayResponse.tokens_used > 0) || selectedQuestionAnswer) && (
                          <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                            <div className="flex items-center gap-4">
                              {displayResponse && 'tokens_used' in displayResponse && displayResponse.tokens_used && displayResponse.tokens_used > 0 && (
                                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  {displayResponse.tokens_used} tokens utilizados
                                </span>
                              )}
                              {selectedQuestionAnswer && (
                                <span className="text-xs px-2 py-1 rounded-full" style={{ 
                                  backgroundColor: 'rgba(139, 92, 246, 0.1)', 
                                  color: '#8b5cf6' 
                                }}>
                                  Resposta do histórico
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4" style={{ color: '#fbbf24' }} />
                              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                Resposta útil?
                              </span>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ) : null}
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Coluna Lateral - 4 colunas */}
        <div className="lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="overflow-hidden sticky top-6 shadow-lg">
              {/* Header do Card */}
              <div
                className="px-5 py-5 border-b flex items-center gap-4"
                style={{
                  borderColor: 'rgba(139, 92, 246, 0.2)',
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.08) 50%, rgba(109, 40, 217, 0.05) 100%)'
                }}
              >
                <div
                  className="p-3 rounded-xl shadow-md"
                  style={{ 
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    boxShadow: '0 4px 14px 0 rgba(139, 92, 246, 0.4)'
                  }}
                >
                  <History className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    Histórico de Perguntas
                  </h3>
                  <p className="text-xs font-medium" style={{ color: '#8b5cf6' }}>
                    {storedQuestions.length} perguntas salvas
                  </p>
                </div>
              </div>

              {/* Busca */}
              <div className="p-4 border-b" style={{ borderColor: 'rgba(139, 92, 246, 0.2)', backgroundColor: 'var(--bg-card)' }}>
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: '#8b5cf6' }}
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar perguntas..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 text-sm focus:outline-none focus:ring-2 shadow-sm transition-all duration-200"
                    style={{
                      backgroundColor: searchTerm ? 'rgba(139, 92, 246, 0.1)' : 'var(--bg-secondary)',
                      borderColor: searchTerm ? '#8b5cf6' : 'var(--border-primary)',
                      color: 'var(--text-primary)',
                      boxShadow: searchTerm ? '0 2px 8px rgba(139, 92, 246, 0.15)' : 'var(--shadow-sm)',
                    }}
                  />
                </div>
              </div>

              {/* Lista de Perguntas */}
              <div className="max-h-[500px] overflow-y-auto">
                {filteredQuestions.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {searchTerm ? 'Nenhuma pergunta encontrada' : 'Nenhuma pergunta salva ainda'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                    {filteredQuestions.map((question, index) => {
                      const isSelected = selectedQuestionId === question.id
                      return (
                        <motion.button
                          key={question.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => handleUseStoredQuestion(question)}
                          className="w-full p-4 text-left transition-all duration-200 group rounded-lg mx-2 my-1"
                          style={{
                            backgroundColor: isSelected ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                            borderLeft: isSelected ? '3px solid #8b5cf6' : '3px solid transparent',
                            transform: isSelected ? 'translateX(4px)' : 'translateX(0)'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'
                              e.currentTarget.style.transform = 'translateX(4px)'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'transparent'
                              e.currentTarget.style.transform = 'translateX(0)'
                            }
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p
                                className="text-sm font-medium line-clamp-2 mb-1"
                                style={{ color: isSelected ? '#8b5cf6' : 'var(--text-primary)' }}
                              >
                                {question.question}
                              </p>
                              <div className="flex items-center gap-2">
                                <span
                                  className="text-xs px-2 py-0.5 rounded-full"
                                  style={{
                                    backgroundColor: isSelected ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-tertiary)',
                                    color: isSelected ? '#8b5cf6' : 'var(--text-secondary)'
                                  }}
                                >
                                  {question.context}
                                </span>
                                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  {formatRelativeTime(question.timestamp)}
                                </span>
                              </div>
                            </div>
                            <ChevronRight
                              className={`w-4 h-4 flex-shrink-0 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                              style={{ color: isSelected ? '#8b5cf6' : 'var(--text-secondary)' }}
                            />
                          </div>
                        </motion.button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Footer com info */}
              <div
                className="px-4 py-3 border-t text-center"
                style={{ 
                  borderColor: 'rgba(139, 92, 246, 0.2)', 
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.03) 100%)'
                }}
              >
                <p className="text-xs font-medium" style={{ color: '#8b5cf6' }}>
                  As perguntas são salvas automaticamente
                </p>
              </div>
            </Card>
            </motion.div>
          </div>
        </div>
    </Page>
  )
}

export default SpecializedQuestionsPage
